import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { academicsMachine } from '../academics.machine';
import { EducationFormValues } from '../types';
import { EducationForm } from './EducationForm';
import { CreateEduCols } from './constants';

interface Props {
  actor: ActorRefFrom<typeof academicsMachine>;
}

const TAGS = CT.academics.education;

const EducationNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.academics}
    title="No education records yet"
    cyTag={TAGS.educationView.emptyState}
  />
);

export const EducationDetails = (props: Props) => {
  const { actor } = props;
  const { loading, modal, educations, educationForm } = useSelector(
    actor,
    (state) => state.context
  );

  const openModal = () => {
    actor.send({ type: 'ON_ADD' });
  };
  const closeModal = () => {
    actor.send({ type: 'ON_CLOSE_MODAL' });
  };

  // [MCV-1238] The `ON_VALIDATE` event name is historical — the child form
  // no longer pre-validates before requesting the detail modal to open.
  // The state machine just flips `modal.detailModal` on receipt; the
  // `values` payload is snapshotted for context only.
  const requestDetailOpen = (values: EducationFormValues) => {
    actor.send({ type: 'ON_VALIDATE', values });
  };

  const closeDetailModal = () => {
    actor.send({ type: 'ON_CLOSE_DETAIL_MODAL' });
  };

  const deleteDetail = (id: number) => {
    actor.send({ type: 'ON_DELETE_DETAIL', payload: { id } });
  };

  const handleEdit = (id: number) => actor.send({ type: 'ON_EDIT', payload: { id } });

  const handleDelete = (id: number) => {
    actor.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id, type: 'EDUCATION' } });
  };

  const handleFormSuccess = (values: EducationFormValues) => {
    if (modal.create) {
      actor.send({ type: 'ON_SUBMIT', values });
    } else {
      actor.send({ type: 'ON_UPDATE', values });
    }
  };

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Education"
        addLabel="Add Education"
        onAddClick={openModal}
        addBtnTag={TAGS.addBtn}
        layoutTag={TAGS.layout}
      >
        <BasicGrid
          newRecordsTable="education"
          data={educations}
          loading={loading}
          colDefs={CreateEduCols({ handleDelete, handleEdit })}
          pagination={true}
          cyTag={TAGS.grid}
          noRowsOverlayComponent={EducationNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        title={'Education History'}
        onClose={closeModal}
        editing={modal.edit}
        creating={modal.create}
      >
        <EducationForm
          handleClose={closeModal}
          handleFormSuccess={handleFormSuccess}
          form={educationForm}
          showDetailModal={modal.detailModal}
          handleDetailClose={closeDetailModal}
          requestDetailOpen={requestDetailOpen}
          deleteDetail={deleteDetail}
        />
      </FormDialog>
    </>
  );
};
